/* eslint-disable react/display-name */
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// material
import {
    useMediaQuery,
    useTheme,
    Box,
    Typography,
    Switch,
    CircularProgress,
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    makeStyles,
} from "@material-ui/core";
import {
    DataGrid,
    GridCheckIcon,
    GridToolbarContainer,
    GridToolbarFilterButton,
} from "@material-ui/data-grid";
import RefreshIcon from "@material-ui/icons/Refresh";

// utils
import { getMustVisitPayload } from "@/utils/students";
import { toasterMessage as TM } from "@/utils/toaster-msg";
import {
    buildBreakoutDisplayName,
    flattenBreakoutParticipants,
    findBreakoutParticipant,
    getBreakoutRoomNames,
    findBestBreakoutRoomMatch,
    getOccupancyForUniversity,
    isParticipantAlreadyInRoom,
    getBreakoutMoveErrorMessage,
    waitForBreakoutMoveConfirmation,
    isCounsellingDeskLogin,
    canBreakoutMoveToUniversity,
    getBreakoutMoveNotesGateMessage,
} from "@/utils/breakout";

// api
import {
    fetchBreakoutBotStatus,
    fetchBreakoutParticipants,
    fetchBreakoutJob,
    ensureBreakoutBotJoined,
    queueBreakoutParticipantMove,
    syncBreakoutParticipantMove,
    fetchOccupancySnapshot,
} from "@/api";

// actions
import { showToasterMessage } from "@/store/actions/commonActions";
import {
    updateMustVisitUniversity,
    clearStudentSearchAfterSuccessfulMove,
} from "@/store/actions/studentDetailsActions";
import { studentNotesGetAndStore } from "@/store/actions/notesActions";
import { getLocalData, setLocalData, removeLocalData } from "@/utils/localStorage";

const OCCUPANCY_POLL_MS = 60000;
const OCCUPANCY_MEETING_STORAGE_KEY = "breakout_occupancy_meeting_number";

// styles
const useStyles = makeStyles(() => ({
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
    },
    roomCountCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 12,
        width: "100%",
        height: "100%",
        overflow: "visible",
    },
    roomCountItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 36,
        gap: 4,
        lineHeight: 1.2,
    },
    roomCountLabel: {
        fontSize: 10,
        fontWeight: 500,
        color: "#757575",
        textTransform: "uppercase",
        letterSpacing: 0.3,
    },
    roomCountValue: {
        fontSize: 13,
        fontWeight: 400,
        color: "#212121",
        fontVariantNumeric: "tabular-nums",
    },
    roomCountStudents: {
        color: "#00695c",
    },
    roomCountEmpty: {
        fontSize: 14,
        color: "#9e9e9e",
        width: "100%",
        textAlign: "center",
    },
    roomCountHeader: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
    },
    roomCountHeaderLabel: {
        fontWeight: 500,
        fontSize: "0.875rem",
    },
    roomCountRefresh: {
        padding: 2,
    },
}));

function CustomToolbar() {
    const classes = useStyles();
    return (
        <GridToolbarContainer className={classes.toolbar}>
            <GridToolbarFilterButton />
        </GridToolbarContainer>
    );
}

export default function DataTable({
    loadAllMustVistCell,
    rows,
    registrantCode,
    firstName,
    lastName,
    allNotes = [],
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const classes = useStyles();
    const [page, setPage] = useState(0);
    const [movingUniversityName, setMovingUniversityName] = useState(null);
    const [confirmMoveUniversity, setConfirmMoveUniversity] = useState(null);
    const [occupancy, setOccupancy] = useState(null);
    const [occupancyRefreshing, setOccupancyRefreshing] = useState(false);
    const occupancyMeetingRef = useRef(
        getLocalData(OCCUPANCY_MEETING_STORAGE_KEY) || null
    );
    const universityDetails = useSelector(
        (state) => state.universityDetails.data
    );
    const notesFromStore = useSelector((state) => state.notes.data) || [];
    const dispatch = useDispatch();

    const studentNotes = useMemo(() => {
        if (Array.isArray(notesFromStore) && notesFromStore.length > 0) {
            return notesFromStore;
        }
        return Array.isArray(allNotes) ? allNotes : [];
    }, [notesFromStore, allNotes]);

    // University login only: keep Notes Provided in sync for Move gating
    useEffect(() => {
        if (!registrantCode || !universityDetails.showPU) {
            return;
        }
        if (isCounsellingDeskLogin(universityDetails)) {
            return;
        }
        if (!universityDetails.universityCode || !universityDetails.fairID) {
            return;
        }
        dispatch(
            studentNotesGetAndStore(registrantCode, {
                university_code: universityDetails.universityCode,
                fair_id: universityDetails.fairID,
                counsellor_access: universityDetails.showPU,
            })
        );
    }, [
        dispatch,
        registrantCode,
        universityDetails.showPU,
        universityDetails.universityCode,
        universityDetails.fairID,
        universityDetails.universityName,
    ]);

    const loadOccupancyFromS3 = useCallback(async (meetingNumber) => {
        const mn =
            meetingNumber ||
            occupancyMeetingRef.current ||
            getLocalData(OCCUPANCY_MEETING_STORAGE_KEY) ||
            null;
        if (!mn) {
            setOccupancy(null);
            return;
        }
        try {
            const snapshot = await fetchOccupancySnapshot(mn);
            setOccupancy(snapshot || null);
        } catch (error) {
            setOccupancy(null);
        }
    }, []);

    const refreshOccupancyCounts = useCallback(async () => {
        if (occupancyRefreshing) {
            return;
        }
        setOccupancyRefreshing(true);
        try {
            await loadOccupancyFromS3(
                occupancyMeetingRef.current ||
                    getLocalData(OCCUPANCY_MEETING_STORAGE_KEY) ||
                    null
            );
        } finally {
            setOccupancyRefreshing(false);
        }
    }, [loadOccupancyFromS3, occupancyRefreshing]);

    // Resolve meeting via GET /bot on mount / page change; poll S3 every 60s with stored id
    useEffect(() => {
        if (!universityDetails.showPU) {
            setOccupancy(null);
            return undefined;
        }

        let cancelled = false;
        let intervalId = null;

        const resolveMeetingAndLoad = async () => {
            try {
                const botResponse = await fetchBreakoutBotStatus();
                if (cancelled) {
                    return;
                }
                const bot = (botResponse && botResponse.data) || {};
                const nextMeeting =
                    bot.meetingNumber || bot.sessionMeetingNumber || null;
                if (nextMeeting) {
                    occupancyMeetingRef.current = nextMeeting;
                    setLocalData(OCCUPANCY_MEETING_STORAGE_KEY, nextMeeting);
                } else {
                    occupancyMeetingRef.current = null;
                    removeLocalData(OCCUPANCY_MEETING_STORAGE_KEY);
                }
            } catch (error) {
                // Keep any previously stored meeting id for S3 poll
                occupancyMeetingRef.current =
                    getLocalData(OCCUPANCY_MEETING_STORAGE_KEY) || null;
            }
            if (!cancelled) {
                await loadOccupancyFromS3(occupancyMeetingRef.current);
            }
        };

        resolveMeetingAndLoad();
        intervalId = window.setInterval(() => {
            loadOccupancyFromS3(
                occupancyMeetingRef.current ||
                    getLocalData(OCCUPANCY_MEETING_STORAGE_KEY) ||
                    null
            );
        }, OCCUPANCY_POLL_MS);

        return () => {
            cancelled = true;
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [universityDetails.showPU, loadOccupancyFromS3]);

    const updatedRows = useMemo(() => {
        return rows.map((item, index) => {
            return { ...item, id: index };
        });
    }, [rows]);

    const canMoveToUniversity = (university) =>
        canBreakoutMoveToUniversity({
            universityDetails,
            targetUniversity: university,
            notes: studentNotes,
        });

    const moveGateTooltip = () => {
        if (isCounsellingDeskLogin(universityDetails)) {
            return "";
        }
        return getBreakoutMoveNotesGateMessage(
            universityDetails.universityName
        );
    };

    const toggleUniversitySwitch = (university, setIsLoading) => {
        setIsLoading(true);
        const payload = getMustVisitPayload([university], registrantCode);
        if (payload) {
            dispatch(updateMustVisitUniversity(updatedRows, payload, setIsLoading));
        } else {
            setIsLoading(false);
            dispatch(showToasterMessage(TM.cantUpdate.msg, TM.cantUpdate.color));
        }
    };

    const studentDisplayName = [firstName, lastName]
        .filter(Boolean)
        .join(" ")
        .trim() || registrantCode;

    const requestBreakoutMove = (university) => {
        if (!university || !university.university_name) {
            dispatch(showToasterMessage("University name is missing", "error"));
            return;
        }
        if (!registrantCode) {
            dispatch(showToasterMessage("Student unique id is missing", "error"));
            return;
        }
        if (!canMoveToUniversity(university)) {
            dispatch(showToasterMessage(moveGateTooltip(), "error"));
            return;
        }
        setConfirmMoveUniversity(university);
    };

    const handleConfirmMoveCancel = () => {
        setConfirmMoveUniversity(null);
    };

    const handleConfirmMoveYes = () => {
        const university = confirmMoveUniversity;
        setConfirmMoveUniversity(null);
        if (university) {
            handleBreakoutMove(university);
        }
    };

    const handleBreakoutMove = async (university) => {
        const toRoomName = university.university_name;
        if (!toRoomName) {
            dispatch(showToasterMessage("University name is missing", "error"));
            return;
        }

        if (!registrantCode) {
            dispatch(showToasterMessage("Student unique id is missing", "error"));
            return;
        }

        if (!canMoveToUniversity(university)) {
            dispatch(showToasterMessage(moveGateTooltip(), "error"));
            return;
        }

        setMovingUniversityName(toRoomName);

        try {
            const botResponse = await fetchBreakoutBotStatus();
            const bot = botResponse.data || {};
            const meetingNumber =
                bot.meetingNumber || bot.sessionMeetingNumber || null;

            if (!meetingNumber) {
                dispatch(
                    showToasterMessage(
                        "Breakout bot has no saved meeting session. Join once from the Breakout Dashboard first.",
                        "error"
                    )
                );
                return;
            }

            // Auto-rejoin when idle / kicked (uses saved session on breakout-api).
            if (!bot.joined) {
                dispatch(
                    showToasterMessage(
                        "Bot not in meeting — rejoining…",
                        "info"
                    )
                );
                await ensureBreakoutBotJoined(meetingNumber);
            }

            const participantsResponse = await fetchBreakoutParticipants(meetingNumber);
            const participantsPayload = participantsResponse.data || {};
            const roomNames = getBreakoutRoomNames(participantsPayload);
            const roomMatch = findBestBreakoutRoomMatch(roomNames, toRoomName);

            if (!roomMatch) {
                dispatch(
                    showToasterMessage(
                        `Breakout room "${toRoomName}" was not found in the live meeting`,
                        "error"
                    )
                );
                return;
            }

            const resolvedRoomName = roomMatch.roomName;

            const liveParticipants = flattenBreakoutParticipants(participantsPayload);
            const fallbackDisplayName = buildBreakoutDisplayName({
                firstName,
                lastName,
                registrantCode,
                country: university.country,
            });
            const matched = findBreakoutParticipant({
                participants: liveParticipants,
                registrantCode,
                fallbackDisplayName,
            });

            if (!matched || !matched.userId) {
                dispatch(
                    showToasterMessage(
                        "Participant was not found in the live Zoom meeting",
                        "error"
                    )
                );
                return;
            }

            if (isParticipantAlreadyInRoom(matched, resolvedRoomName)) {
                dispatch(
                    showToasterMessage(
                        `Participant is already in ${resolvedRoomName}`,
                        "error"
                    )
                );
                return;
            }

            const movePayload = {
                userId: matched.userId,
                displayName: matched.displayName,
                fromRoomName: matched.currentRoomName,
                toRoomName: resolvedRoomName,
            };

            let jobId = null;

            try {
                const queueResponse = await queueBreakoutParticipantMove(
                    meetingNumber,
                    movePayload
                );
                jobId = queueResponse.data && queueResponse.data.jobId;
            } catch (queueError) {
                // SQS unavailable → fall back to sync once
                await syncBreakoutParticipantMove(meetingNumber, movePayload);
                dispatch(
                    showToasterMessage(
                        resolvedRoomName === toRoomName
                            ? `Moved to ${resolvedRoomName}`
                            : `Moved to ${resolvedRoomName} (matched from ${toRoomName})`,
                        "success"
                    )
                );
                dispatch(clearStudentSearchAfterSuccessfulMove(registrantCode));
                return;
            }

            // Poll every 5s; if still pending after 30s, unlock UI for another move
            const confirmation = await waitForBreakoutMoveConfirmation({
                jobId,
                fetchJob: fetchBreakoutJob,
                intervalMs: 5000,
                maxPendingWaitMs: 30000,
                maxProcessingWaitMs: 60000,
                maxFailures: 3,
            });

            if (confirmation.ok) {
                dispatch(
                    showToasterMessage(
                        resolvedRoomName === toRoomName
                            ? `Moved to ${resolvedRoomName}`
                            : `Moved to ${resolvedRoomName} (matched from ${toRoomName})`,
                        "success"
                    )
                );
                dispatch(clearStudentSearchAfterSuccessfulMove(registrantCode));
            } else {
                dispatch(
                    showToasterMessage(
                        confirmation.error ||
                            "Something went wrong while moving the participant. Please try again.",
                        "error"
                    )
                );
            }
        } catch (error) {
            dispatch(
                showToasterMessage(
                    getBreakoutMoveErrorMessage(error, { toRoomName }),
                    "error"
                )
            );
        } finally {
            setMovingUniversityName(null);
        }
    };

    const columns = [
        {
            field: "university_name",
            headerName: "University",
            width: isMobile ? 200 : 300,
            renderCell: (value) => {
                const university = value.row;
                return (
                    <div
                        style={{
                            padding: "10px 10px",
                            width: isMobile ? "200px" : "320px",
                            height: "80px",
                            whiteSpace: "normal",
                            lineHeight: 1.5,
                        }}
                    >
                        <Typography variant="h4">{university.university_name}</Typography>
                    </div>
                );
            },
        },
        {
            field: "country",
            headerName: "Country",
            width: isMobile ? 120 : 140,
        },
        {
            field: "must_visit",
            headerName: "Must Visit",
            type: "boolean",
            width: isMobile ? 120 : 160,
            ...(universityDetails.showPU
                ? {
                    renderCell: (value) => {
                        const [isLoading, setIsloading] = useState(false);
                        const university = value.row;

                        if (university.attendance && university.must_visit) {
                            return (
                                <GridCheckIcon
                                    color="primary"
                                    fontSize="small"
                                    data-value="true"
                                    className="MuiDataGrid-booleanCell"
                                />
                            );
                        }

                        return (
                            <Box>
                                {loadAllMustVistCell || isLoading ? (
                                    <Box
                                        display={"flex"}
                                        alignItems={"center"}
                                        width="100%"
                                        height="100%"
                                    >
                                        <CircularProgress color="primary" size={30} />
                                    </Box>
                                ) : (
                                    <Switch
                                        color={"primary"}
                                        checked={university.must_visit}
                                        onChange={() =>
                                            toggleUniversitySwitch(university, setIsloading)
                                        }
                                    />
                                )}
                            </Box>
                        );
                    },
                }
                : {}),
        },
        {
            field: "attendance",
            headerName: "Attended",
            type: "boolean",
            width: isMobile ? 120 : 150,
        },
    ];

    if (universityDetails.showPU) {
        columns.push({
            field: "desk_number",
            headerName: "Desk Number",
            width: isMobile ? 120 : 190,
            renderCell: (value) => {
                const university = value.row;
                return (
                    <Typography
                        variant="h4"
                        style={{ width: "100%", textAlign: "center" }}
                    >
                        {university.desk_number ? university.desk_number : "-"}
                    </Typography>
                );
            },
        });

        columns.push({
            field: "room_occupancy",
            headerName: "In room",
            width: isMobile ? 130 : 150,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: () => (
                <div className={classes.roomCountHeader}>
                    <span className={classes.roomCountHeaderLabel}>In room</span>
                    <Tooltip title="Refresh room counts">
                        <span>
                            <IconButton
                                className={classes.roomCountRefresh}
                                size="small"
                                color="primary"
                                aria-label="Refresh room counts"
                                disabled={occupancyRefreshing}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    refreshOccupancyCounts();
                                }}
                            >
                                {occupancyRefreshing ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : (
                                    <RefreshIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            ),
            renderCell: (value) => {
                const university = value.row;
                const counts = getOccupancyForUniversity(
                    occupancy,
                    university.university_name
                );
                if (!counts) {
                    return (
                        <span className={classes.roomCountEmpty}>—</span>
                    );
                }
                return (
                    <div
                        className={classes.roomCountCell}
                        title={`${counts.roomName || university.university_name}: ${counts.allCount} total, ${counts.studentCount} students`}
                    >
                        <div className={classes.roomCountItem}>
                            <span className={classes.roomCountLabel}>
                                Total
                            </span>
                            <span className={classes.roomCountValue}>
                                {counts.allCount}
                            </span>
                        </div>
                        <div className={classes.roomCountItem}>
                            <span className={classes.roomCountLabel}>
                                Students
                            </span>
                            <span
                                className={`${classes.roomCountValue} ${classes.roomCountStudents}`}
                            >
                                {counts.studentCount}
                            </span>
                        </div>
                    </div>
                );
            },
        });

        columns.push({
            field: "breakout_move",
            headerName: "Move to room",
            width: isMobile ? 140 : 160,
            sortable: false,
            filterable: false,
            renderCell: (value) => {
                const university = value.row;
                const isMoving = movingUniversityName === university.university_name;
                const notesOk = canMoveToUniversity(university);
                const disabled =
                    isMoving || !!movingUniversityName || !notesOk;
                const button = (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={disabled}
                        onClick={() => requestBreakoutMove(university)}
                    >
                        {isMoving ? (
                            <CircularProgress color="primary" size={20} />
                        ) : (
                            "Move to room"
                        )}
                    </Button>
                );

                if (!notesOk) {
                    return (
                        <Tooltip title={moveGateTooltip()}>
                            <span>{button}</span>
                        </Tooltip>
                    );
                }

                return button;
            },
        });
    }

    return (
        <div
            style={{
                height: updatedRows.length
                    ? updatedRows.length <= 100
                        ? 220 + updatedRows.length * 80
                        : page === 0
                            ? 220 + 99 * 80
                            : 220 + (updatedRows.length - page * 100) * 80
                    : 500,
                width: "100%",
            }}
        >
            <DataGrid
                components={{
                    Toolbar: CustomToolbar,
                }}
                page={page}
                onPageChange={(page) => {
                    window.scrollTo(0, 0);
                    setPage(page);
                }}
                disableColumnMenu
                rowHeight={80}
                rows={updatedRows}
                columns={columns}
            />
            <Dialog
                open={!!confirmMoveUniversity}
                onClose={handleConfirmMoveCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm move</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Move{" "}
                        <strong>
                            {studentDisplayName}
                            {registrantCode ? ` (${registrantCode})` : ""}
                        </strong>{" "}
                        to{" "}
                        <strong>
                            {confirmMoveUniversity
                                ? confirmMoveUniversity.university_name
                                : ""}
                        </strong>
                        ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmMoveCancel} color="default">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmMoveYes}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Yes, move
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

DataTable.propTypes = {
    loadAllMustVistCell: PropTypes.bool,
    rows: PropTypes.array.isRequired,
    registrantCode: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    allNotes: PropTypes.array,
};
